import React, { FC } from 'react';
import { defineLoader, httpTaskToResponseTask } from '@core/router/loader';
import { parseQueriesFormUrl } from '@shared/utils/queries';
import * as MetersService from '@modules/water/meters/service';
import { getPageQuery } from '@shared/modules/range/utils';
import { SearchFilter, searchFilterParser } from '@shared/modules/filter';
import { useRange, VirtualizedList } from '@shared/modules/range';
import { identity } from 'fp-ts/function';
import Page, { PageProps } from '@layout/page/Page';
import { defineRoute } from '@core/router';
import { SharedButtonLink } from '@styles/shared';
import { Water } from '@modules/water/model';
import MeterListRow from '@modules/water/meters/components/list/row/MeterListRow';
import { Stack, Text, ThemeIcon } from '@mantine/core';
import { IconDashboard } from '@tabler/icons-react';
import Meter = Water.Meter;
import { CompanyUserRole } from '@modules/auth/model';
import { useHasRole } from '@modules/auth/hooks';

const loader = defineLoader({
  handler: ({ request }) => {
    const queries = parseQueriesFormUrl(request.url);
    return httpTaskToResponseTask(
      MetersService.getWaterMetersRange(getPageQuery(queries), searchFilterParser(queries)),
    );
  },
});

const MetersListPage: FC = () => {
  const { range, handleLoadPage } = useRange<typeof loader, Meter.Range.Item, SearchFilter>(identity);
  const isAdmin = useHasRole(CompanyUserRole.Admin);

  const pageProps: PageProps = {
    seoTitle: 'Vos compteurs',
    back: { title: 'Vos compteurs', to: '/water' },
    bottomBar: isAdmin ? (
      <SharedButtonLink to="new" btnType="create">
        Déclarer un compteur
      </SharedButtonLink>
    ) : undefined,
  };

  return (
    <Page {...pageProps} bg="background.1" withNav>
      <VirtualizedList
        range={range}
        loadPage={handleLoadPage}
        emptyRender={
          <Stack align="center" ta="center" spacing="lg">
            <ThemeIcon size={95} color="transparent" c="blue.2">
              <IconDashboard size="100%" />
            </ThemeIcon>
            <Text weight={500}>Vous n’avez aucun compteur déclaré actuellement</Text>
          </Stack>
        }
      >
        {MeterListRow}
      </VirtualizedList>
    </Page>
  );
};

const metersListRoute = defineRoute({
  component: MetersListPage,
  loader,
});

export default metersListRoute;
